import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { TenantProvider, useTenant } from "./context/TenantContext";
import { SimulationProvider } from "./context/SimulationContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";

// Pages - Public
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import EunacomLandingPage from "./pages/EunacomLandingPage";
import EunacomDashboardPage from "./pages/EunacomDashboardPage";
import TrialExpiredPage from "./pages/TrialExpiredPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";

// Pages - Main App
import DashboardPage from "./pages/DashboardPage";
import ScenariosPage from "./pages/ScenariosPage";
import ScenarioDetailPage from "./pages/ScenarioDetailPage";
import SimulationPage from "./pages/SimulationPage";
import DebriefingPage from "./pages/DebriefingPage";
import HistoryPage from "./pages/HistoryPage";
import EvaluationsPage from "./pages/EvaluationsPage";
import OSCELivePage from "./pages/OSCELivePage";

// Pages - Admin
import MedicationsPage from "./pages/admin/MedicationsPage";
import ClinicalActionsPage from "./pages/admin/ClinicalActionsPage";
import UsersPage from "./pages/admin/UsersPage";
import ScenarioEditorPage from "./pages/admin/ScenarioEditorPage";
import ProtocolImportPage from "./pages/admin/ProtocolImportPage";
import EcgRhythmsPage from "./pages/admin/EcgRhythmsPage";
import EstablishmentSettingsPage from "./pages/admin/EstablishmentSettingsPage";
import EstablishmentsPage from "./pages/admin/EstablishmentsPage";
import RolesPage from "./pages/admin/RolesPage";
import SimulationLevelsPage from "./pages/admin/SimulationLevelsPage";
import AuditPage from "./pages/admin/AuditPage";
import ProtocolsAdminPage from "./pages/ProtocolsAdminPage";
import DiagnosesPage from "./pages/admin/DiagnosesPage";
import TreatmentPlansPage from "./pages/admin/TreatmentPlansPage";
import BulkImportCenterPage from "./pages/admin/BulkImportCenterPage";
import LandingDemoPage from "./pages/admin/LandingDemoPage";
import InstitutionalVideosPage from "./pages/admin/InstitutionalVideosPage";
import DemoStandPage from "./pages/DemoStandPage";
import DemoCapturePage from "./pages/DemoCapturePage";
import AdministrationRoutesPage from "./pages/admin/AdministrationRoutesPage";
import ReportsPage from "./pages/admin/ReportsPage";
import MedicationPresentationsPage from "./pages/admin/MedicationPresentationsPage";
import PlansManagementPage from "./pages/admin/PlansManagementPage";
import AnalyticsPage from "./pages/admin/AnalyticsPage";
import CommercialManagementPage from "./pages/admin/CommercialManagementPage";
import SettingsPage from "./pages/admin/SettingsPage";
import PhysiologicalEffectsCatalogPage from "./pages/admin/PhysiologicalEffectsCatalogPage";
import SoftSkillsPlayerPage from "./pages/SoftSkillsPlayerPage";
import SoftSkillsDebriefPage from "./pages/SoftSkillsDebriefPage";
import SoftSkillsProtocolsPage from "./pages/admin/SoftSkillsProtocolsPage";
import XRAdminPage from "./pages/admin/XRAdminPage";
import XRRankingPage from "./pages/admin/XRRankingPage";
import XRLinkDevicePage from "./pages/XRLinkDevicePage";
import XRSessionPlayerPage from "./pages/xr/XRSessionPlayerPage";
import XRPWAHome from "./pages/xr/XRPWAHome";
import XRCertificateVerifyPage from "./pages/XRCertificateVerifyPage";

// Pages - Instructor
import CoursesPage from "./pages/instructor/CoursesPage";
import MyStudentsPage from "./pages/instructor/MyStudentsPage";
import LiveSupervisionPage from "./pages/instructor/LiveSupervisionPage";
import SupervisionPageV2 from "./pages/instructor/SupervisionPageV2";
import SupervisionHistoryPage from "./pages/instructor/SupervisionHistoryPage";

// Auth redirect component - redirects authenticated users away from public pages
const AuthRedirect = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />;
  return children;
};

// Legacy redirect component - handles parameter passing from old routes to new /app/* routes
const LegacyRedirect = ({ to, paramName = 'id' }) => {
  const params = useParams();
  const paramValue = params[paramName] || params.id;
  const targetPath = paramValue ? `${to}/${paramValue}` : to;
  return <Navigate to={targetPath} replace />;
};

// Protected wrapper for AppLayout - ensures auth before rendering layout
const ProtectedAppLayout = () => {
  return (
    <ProtectedRoute>
      <AppLayout />
    </ProtectedRoute>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* ==================== PUBLIC ROUTES ==================== */}
      {/* Landing and Auth */}
      <Route path="/" element={
        <AuthRedirect>
          <LandingPage />
        </AuthRedirect>
      } />
      <Route path="/login" element={
        <AuthRedirect>
          <LoginPage />
        </AuthRedirect>
      } />
      <Route path="/register" element={
        <AuthRedirect>
          <RegisterPage />
        </AuthRedirect>
      } />
      <Route path="/forgot-password" element={
        <AuthRedirect>
          <ForgotPasswordPage />
        </AuthRedirect>
      } />
      <Route path="/cotizar" element={<QuoteRequestPage />} />
      
      {/* Payment Routes (public - need to work without auth for callbacks) */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

      {/* SimuClinic XR — PWA Landing + Public device linking (token en URL, sin auth previa) */}
      <Route path="/xr" element={<XRPWAHome />} />
      <Route path="/xr/session/:token" element={<XRSessionPlayerPage />} />
      <Route path="/xr/certificate/verify/:id" element={<XRCertificateVerifyPage />} />
      
      {/* EUNACOM Public Module */}
      <Route path="/eunacom" element={<EunacomLandingPage />} />
      <Route path="/demo-stand" element={<DemoStandPage />} />
      <Route path="/demo-capture" element={<DemoCapturePage />} />
      <Route path="/eunacom/dashboard" element={<EunacomDashboardPage />} />
      
      {/* Trial Expired (accessible when trial expires) */}
      <Route path="/trial-expired" element={<TrialExpiredPage />} />

      {/* ==================== PROTECTED ROUTES (with AppLayout) ==================== */}
      {/* 
        All private routes are nested under /app with AppLayout as parent.
        This ensures the sidebar and header remain visible when navigating.
        Only the <Outlet /> (main content) changes.
      */}
      <Route path="/app" element={<ProtectedAppLayout />}>
        {/* Dashboard - Default route */}
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Scenarios & Simulations */}
        <Route path="scenarios" element={<ScenariosPage />} />
        <Route path="scenarios/:id" element={<ScenarioDetailPage />} />
        <Route path="simulation/:id" element={<SimulationPage />} />
        <Route path="debriefing/:sessionId" element={<DebriefingPage />} />
        <Route path="osce/session/:sessionId" element={<OSCELivePage />} />
        
        {/* User Pages */}
        <Route path="history" element={<HistoryPage />} />
        <Route path="evaluations" element={<EvaluationsPage />} />
        <Route path="profile" element={<DashboardPage />} /> {/* TODO: Create ProfilePage */}
        
        {/* Instructor Routes */}
        <Route path="instructor/my-students" element={<MyStudentsPage />} />
        <Route path="instructor/live-supervision" element={<LiveSupervisionPage />} />
        <Route path="instructor/supervision" element={<SupervisionPageV2 />} />
        <Route path="instructor/supervision-history" element={<SupervisionHistoryPage />} />
        <Route path="instructor/courses" element={<CoursesPage />} />
        
        {/* Admin - Mantenedores */}
        <Route path="admin/medications" element={<MedicationsPage />} />
        <Route path="admin/administration-routes" element={<AdministrationRoutesPage />} />
        <Route path="admin/medication-presentations" element={<MedicationPresentationsPage />} />
        <Route path="admin/actions" element={<ClinicalActionsPage />} />
        <Route path="soft-skills/play/:scenarioId" element={<SoftSkillsPlayerPage />} />
        <Route path="soft-skills/debrief/:sessionId" element={<SoftSkillsDebriefPage />} />
        <Route path="admin/soft-skills-protocols" element={<SoftSkillsProtocolsPage />} />
        <Route path="admin/xr" element={<XRAdminPage />} />
        <Route path="admin/xr/ranking" element={<XRRankingPage />} />
        <Route path="xr/link" element={<XRLinkDevicePage />} />
        <Route path="admin/physiological-effects" element={<PhysiologicalEffectsCatalogPage />} />
        <Route path="admin/diagnoses" element={<DiagnosesPage />} />
        <Route path="admin/treatment-plans" element={<TreatmentPlansPage />} />
        <Route path="admin/scenarios/catalog" element={<ScenarioEditorPage />} />
        <Route path="admin/scenarios/new" element={<ScenarioEditorPage />} />
        <Route path="admin/protocol-import" element={<ProtocolImportPage />} />
        <Route path="admin/ecg-rhythms" element={<EcgRhythmsPage />} />
        <Route path="admin/establishment-settings" element={<EstablishmentSettingsPage />} />
        <Route path="admin/scenarios/:id/edit" element={<ScenarioEditorPage />} />
        <Route path="admin/protocols" element={<ProtocolsAdminPage />} />
        <Route path="admin/bulk-import" element={<BulkImportCenterPage />} />
        <Route path="admin/landing-demo" element={<LandingDemoPage />} />
        <Route path="admin/videos" element={<InstitutionalVideosPage />} />
        <Route path="admin/reports" element={<ReportsPage />} />
        
        {/* Admin - Administración */}
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/establishments" element={<EstablishmentsPage />} />
        <Route path="admin/roles" element={<RolesPage />} />
        <Route path="admin/simulation-levels" element={<SimulationLevelsPage />} />
        <Route path="admin/audit" element={<AuditPage />} />
        
        {/* SaaS Admin Routes */}
        <Route path="admin/plans" element={<PlansManagementPage />} />
        <Route path="admin/analytics" element={<AnalyticsPage />} />
        <Route path="admin/commercial" element={<CommercialManagementPage />} />
        <Route path="admin/settings" element={<SettingsPage />} />
      </Route>

      {/* ==================== LEGACY ROUTE REDIRECTS ==================== */}
      {/* Redirect old routes to new /app/* structure */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/scenarios" element={<Navigate to="/app/scenarios" replace />} />
      <Route path="/scenarios/:id" element={<LegacyRedirect to="/app/scenarios" />} />
      <Route path="/simulation/:id" element={<LegacyRedirect to="/app/simulation" />} />
      <Route path="/debriefing/:sessionId" element={<LegacyRedirect to="/app/debriefing" paramName="sessionId" />} />
      <Route path="/history" element={<Navigate to="/app/history" replace />} />
      <Route path="/evaluations" element={<Navigate to="/app/evaluations" replace />} />
      <Route path="/reports" element={<Navigate to="/app/history" replace />} />
      <Route path="/simulation" element={<Navigate to="/app/scenarios" replace />} />
      <Route path="/admin/*" element={<Navigate to="/app/admin" replace />} />
      <Route path="/instructor/*" element={<Navigate to="/app/instructor" replace />} />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function TenantGate({ children }) {
  const { tenant, loading } = useTenant();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Cargando…</p>
        </div>
      </div>
    );
  }
  if (tenant?.type === 'not_found' || tenant?.type === 'inactive') {
    const inactive = tenant.type === 'inactive';
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="tenant-error-page">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-amber-50 mx-auto flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900">
            {inactive ? 'Acceso no disponible' : 'Establecimiento no encontrado'}
          </h1>
          <p className="text-sm text-slate-600">
            {inactive
              ? 'Este establecimiento no se encuentra activo actualmente.'
              : 'El acceso institucional solicitado no se encuentra configurado.'}
          </p>
          <a
            href="https://simuclinic.cl"
            className="inline-block px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-700"
            data-testid="go-to-simuclinic-btn"
          >
            Ir a SimuClinic
          </a>
          {tenant.tenant && (
            <p className="text-xs text-slate-400 pt-2">
              Solicitud: <code>{tenant.tenant}</code>
            </p>
          )}
        </div>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <TenantGate>
          <AuthProvider>
            <BrandingProvider>
              <SimulationProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
              </SimulationProvider>
            </BrandingProvider>
          </AuthProvider>
        </TenantGate>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
